import Editor from "@monaco-editor/react";
import React, { useRef, useState } from "react";
import { useFillPage } from "./Form/pageSlack";

/**
 * Monaco as a form control: it takes a value and hands back text, so it can be
 * a plain `<Field name=…>` child like any input.
 *
 * The frame is inline rather than in a stylesheet because callers override it —
 * antd's form theme borders nothing like Monaco's default does — and an inline
 * rule cannot be out-specified from CSS. `style` merges over the defaults.
 */
const MonacoEditorWrapper = ({
    value,
    onChange,
    readOnly = false,
    height = "400px",
    className,
    style,
    resizable = false,
}: {
    value?: string;
    onChange?: (value: string | undefined) => void;
    /**
     * Monaco is not an antd control, so it never sees the `disabled` a form
     * sets on its fields — a read-only form has to say so here.
     */
    readOnly?: boolean;
    height?: string | number;
    className?: string;
    style?: React.CSSProperties;
    /**
     * The frame takes the height and the editor fills it: the frame opens as
     * tall as the page has room for (on a NetorcaForm `fill`) and is dragged
     * taller or shorter by its corner, with the editor following it.
     */
    resizable?: boolean;
}) => {
    const frame = useRef<HTMLDivElement>(null);
    const [grown, setGrown] = useState<number | null>(null);

    useFillPage(() => (resizable ? frame.current : null), (slack) => {
        if (frame.current) setGrown(frame.current.offsetHeight + slack);
    });

    return (
        <div
            ref={frame}
            className={className}
            style={{
                border: '1px solid #d9d9d9',
                borderRadius: '2px',
                backgroundColor: '#fafafa',
                overflow: 'hidden',
                ...style,
                ...(resizable ? {
                    height: grown ?? height,
                    minHeight: 120,
                    resize: 'vertical' as const,
                } : {}),
            }}
        >
            <Editor
                height={resizable ? '100%' : height}
                defaultLanguage="plaintext"
                theme="vs-light"
                value={value}
                onChange={onChange}
                options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    wordWrap: 'on',
                    lineNumbers: 'off',
                    trimAutoWhitespace: false,
                    readOnly,
                    // a frame the reader resizes is one the editor has to follow
                    automaticLayout: resizable,
                    /* Monaco scrolls on past the last line by default, so even
                       a one-line document scrolled under the pointer — the
                       text sliding up out of an editor that had room for it. */
                    scrollBeyondLastLine: false,
                    // with nothing left to scroll, the wheel is the page's
                    scrollbar: { alwaysConsumeMouseWheel: false },
                }}
            />
        </div>
    );
};

export default MonacoEditorWrapper;
